/**
 * Humm "from" price hydration.
 *
 * Collection cards and product-page finance blocks carry data attributes:
 *   - Collection card: <span class="humm-from" data-humm-price="{{ product.price }}">…</span>
 *   - Product page:    <div data-humm-pdp data-humm-price="{{ current_variant.price }}"> … .last-monthly-payment etc … </div>
 *
 * We fetch humm's Widget API once per unique price (deduped + cached), then fill every
 * matching element. It re-runs whenever the Booster theme injects new cards via its
 * `booster:content:update` event (collection filter / sort / pagination) — no
 * MutationObserver and no parent-walking required.
 *
 * Prices in the data attributes are in pennies (Shopify); the humm API expects pounds.
 */

const HUMM_WIDGET_URL = 'https://hhq130ei45.execute-api.eu-west-1.amazonaws.com/prod/widgets/001Tl000008S447IAC';

// price-in-pounds -> shared Promise<Array> of humm plan tiers (data.data).
// A shared promise means N elements at the same price trigger ONE network request
// (dedupes identical cards and the product page's duplicated sticky-ATC copy).
const hummCache = new Map();

function timeoutSignal(ms) {
    return (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined;
}

function getHummData(pounds) {
    if (!hummCache.has(pounds)) {
        const request = fetch(`${HUMM_WIDGET_URL}/${pounds}`, { signal: timeoutSignal(4000) })
            .then((response) => {
                if (!response.ok) throw new Error(`humm ${response.status}`);
                return response.json();
            })
            .then((payload) => payload.data)
            .catch((error) => {
                hummCache.delete(pounds); // drop the failed result so a later pass can retry
                throw error;
            });
        hummCache.set(pounds, request);
    }
    return hummCache.get(pounds);
}

const asMoney = (amount) => '£' + Number(amount).toFixed(2);
const asDesc = (tier) => `${tier.offerDesc} (<strong>${tier.interestRate}% interest</strong>) with humm | kandoo. Apply at checkout.`;

// Collection card: show the lowest monthly payment (the "from" amount).
function fillCard(el, tiers) {
    const amount = el.querySelector('.humm-from-amount') || el;
    amount.textContent = asMoney(tiers[tiers.length - 1].intAmt);
}

// Product page: fill the lowest-monthly ("last") and lowest-interest ("first") rows.
function fillProductPage(container, tiers) {
    const first = tiers[0];
    const last = tiers[tiers.length - 1];
    const set = (selector, apply) => {
        const node = container.querySelector(selector);
        if (node) apply(node);
    };
    set('.last-monthly-payment', (n) => { n.textContent = asMoney(last.intAmt); });
    set('.last-monthly-payment-freq', (n) => { n.innerHTML = last.frequency; });
    set('.last-monthly-payment-desc', (n) => { n.innerHTML = asDesc(last); });
    set('.first-monthly-payment', (n) => { n.textContent = asMoney(first.intAmt); });
    set('.first-monthly-payment-freq', (n) => { n.innerHTML = first.frequency; });
    set('.first-monthly-payment-desc', (n) => { n.innerHTML = asDesc(first); });
}

function hydrateHumm(root = document) {
    const targets = [
        ...root.querySelectorAll('.humm-from[data-humm-price]:not([data-humm-done])'),
        ...root.querySelectorAll('[data-humm-pdp][data-humm-price]:not([data-humm-done])'),
    ];

    targets.forEach((el) => {
        el.dataset.hummDone = '';
        const pounds = Math.floor(Number(el.dataset.hummPrice) / 100);
        if (!pounds) return;

        const isProductPage = el.hasAttribute('data-humm-pdp');
        getHummData(pounds)
            .then((tiers) => {
                if (isProductPage) fillProductPage(el, tiers);
                else fillCard(el, tiers);
            })
            .catch(() => {
                if (isProductPage) {
                    delete el.dataset.hummDone; // leave the server-rendered markup; allow a later retry
                } else {
                    el.textContent = 'FINANCE AVAILABLE'; // graceful fallback instead of a stuck "…"
                }
            });
    });
}

// Initial pass (this script is deferred, so the DOM is already parsed).
hydrateHumm();

// Re-hydrate cards the theme injects via AJAX filter / sort / pagination.
document.addEventListener('booster:content:update', () => hydrateHumm());
